import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Loader2 } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGalleryPhotos();
  }, []);

  const fetchGalleryPhotos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setGalleryPhotos(data);
      }
    } catch (error) {
      console.error('Error fetching gallery photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = filter === 'all'
    ? galleryPhotos
    : galleryPhotos.filter(img => img.category === filter);

  return (
    <section id="gallery" className="py-24 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2 mb-4">
            <span className="text-red-500 font-semibold text-sm">Our Work</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Showcase Gallery
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            See the transformation we bring to every vehicle
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              className={filter === 'all'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'border-zinc-700 text-gray-400 hover:text-white hover:border-red-500'
              }
            >
              All Work
            </Button>
            <Button
              onClick={() => setFilter('exterior')}
              variant={filter === 'exterior' ? 'default' : 'outline'}
              className={filter === 'exterior'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'border-zinc-700 text-gray-400 hover:text-white hover:border-red-500'
              }
            >
              Exterior
            </Button>
            <Button
              onClick={() => setFilter('interior')}
              variant={filter === 'interior' ? 'default' : 'outline'}
              className={filter === 'interior'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'border-zinc-700 text-gray-400 hover:text-white hover:border-red-500'
              }
            >
              Interior
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
            <span className="ml-3 text-gray-400">Loading gallery...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && galleryPhotos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No gallery photos yet. Check back soon!</p>
          </div>
        )}

        {/* Gallery Grid */}
        {!loading && filteredImages.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image) => (
            <div
              key={image.id}
              onClick={() => setSelectedImage(image)}
              className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white text-xl font-bold">{image.title}</h3>
                  <p className="text-red-500 text-sm uppercase font-semibold">{image.category}</p>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Empty Filter State */}
        {!loading && galleryPhotos.length > 0 && filteredImages.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No photos in this category yet.</p>
          </div>
        )}

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-red-500 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-5xl w-full">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-auto rounded-lg"
              />
              <div className="text-center mt-4">
                <h3 className="text-white text-2xl font-bold">{selectedImage.title}</h3>
                <p className="text-red-500 text-sm uppercase font-semibold mt-2">
                  {selectedImage.category}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
